import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { AppLanguage, AVAILABLE_LANGS, isSupportedLang } from './i18n.config';

/**
 * The single choke point for the active UI language. Everything that reads or
 * changes the language goes through here rather than touching TranslocoService
 * directly — so when the source of truth moves (localStorage today; a per-user
 * `/me` field or a Keycloak `locale` claim later), only this service and its
 * caller in ContextService change. The persist-lang plugin transparently writes
 * the choice to localStorage, so we don't persist anything by hand.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly transloco = inject(TranslocoService);

  /** The languages offered in the UI. */
  readonly available: readonly AppLanguage[] = AVAILABLE_LANGS;

  /** The currently active language id, reactive to runtime switches. */
  readonly current: Signal<string> = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  /** Switch the active language. No-op for unsupported ids. */
  setLanguage(lang: string): void {
    if (!isSupportedLang(lang)) {
      return;
    }
    this.transloco.setActiveLang(lang);
  }
}
